import { logger, trackEvent } from "@justhome/common";
import { Lead, usePostLead } from "@justhome/common/api/core";
import { FathomEvent } from "@lib/constants";
import useLinks from "@lib/hooks/useLinks";
import useLocale from "@lib/hooks/useLocale";
import { localeToLanguageISOCode } from "@lib/utilities";
import { getOrigin } from "@lib/utm";
import ApplicationLayout from "@modules/application/components/ApplicationLayout";
import { OptInFormData } from "@modules/application/components/OptInForm";
import useApplicationContext from "@modules/application/hooks/useApplicationContext";
import { useRouter } from "next/router";
import { FC, useCallback } from "react";
import OptInComponent from "./components/OptInComponent";

const OptInAfter: FC = () => {
  const { state, dispatch } = useApplicationContext();
  const locale = useLocale();
  const router = useRouter();

  const { getMeetingLink } = useLinks();
  const { isLoading, isError, mutate } = usePostLead({
    onSuccess(lead) {
      dispatch({ type: "SET_LEAD", lead });
      trackEvent(FathomEvent.BOOK_CALL_INTENTION);
      router.push(getMeetingLink());
    },
    onError: (error) => {
      logger.error("Error while posting a lead.", error);
    },
  });

  const saveLead = useCallback(
    async (optInForm: OptInFormData, applicationId?: string) => {
      const utmOrigin = getOrigin();
      const origin = utmOrigin ? [utmOrigin] : [];
      const body: Lead = {
        email: optInForm.email,
        firstName: optInForm.firstName,
        lastName: optInForm.lastName,
        phone: optInForm.phone,
        language: localeToLanguageISOCode(locale),
        applicationId,
        origin,
        action: "CALL",
      };
      mutate({ body });
    },
    [locale, mutate],
  );

  const handleSubmit = useCallback(
    async (optInForm: OptInFormData) => {
      saveLead(
        optInForm,
        state.application?.id || (router.query.applicationId as string),
      );
    },
    [saveLead, state.application?.id, router.query.applicationId],
  );

  return (
    <ApplicationLayout>
      <OptInComponent
        isError={isError}
        isLoading={isLoading}
        defaultValues={state.lead}
        onSubmit={handleSubmit}
        optInType="after"
      />
    </ApplicationLayout>
  );
};

export default OptInAfter;
